import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Tag } from "antd";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import message from "@/utils/globalMessage";
import PageHeader from "@components/Table/PageHeader";
import ResizableProTable from "@components/Table/ResizableProTable";
import ResponsiveSelect from "@components/ResponsiveSelect";
import {
  SERVICE_REQUEST_STATUS_COLORS,
  normalizeServiceRequestResponse,
} from "./constants";
import "./styles.css";

export default function ServiceRequestList({
  facilityIds = [],
  facilities = [],
  onFacilityChange,
  loadRequests,
  getDetailPath,
  createPath,
  showFacilityColumn = false,
  showRequesterColumn = false,
}) {
  const navigate = useNavigate();
  const actionRef = useRef();

  const request = useCallback(async (params) => {
    if (!facilityIds.length) {
      return { data: [], total: 0, success: true };
    }
    try {
      const response = await loadRequests(facilityIds, {
        page: params.current,
        page_size: params.pageSize,
      });
      const { rows, total } = normalizeServiceRequestResponse(response);
      return { data: rows, total, success: true };
    } catch (error) {
      message.error("Failed to load service requests");
      return { data: [], total: 0, success: false };
    }
  }, [facilityIds, loadRequests]);

  useEffect(() => {
    actionRef.current?.reload?.();
  }, [facilityIds]);

  const openDetail = useCallback((record) => {
    navigate(getDetailPath(record));
  }, [getDetailPath, navigate]);

  const columns = useMemo(() => [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 220,
      render: (value, record) => (
        <button
          type="button"
          className="service-request-table-link"
          onClick={() => openDetail(record)}
        >
          {value}
        </button>
      ),
    },
    ...(showFacilityColumn ? [{
      title: "Facility",
      dataIndex: "facility_name",
      key: "facility_name",
      width: 180,
    }] : []),
    { title: "Suite / Area", dataIndex: "suite_area", key: "suite_area", width: 150 },
    { title: "Category", dataIndex: "category_name", key: "category_name", width: 150 },
    { title: "Priority", dataIndex: "priority", key: "priority", width: 110 },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    ...(showRequesterColumn ? [{
      title: "Requested By",
      dataIndex: "createdby_name",
      key: "createdby_name",
      width: 160,
    }] : []),
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 115,
      render: (value) => (
        <Tag color={SERVICE_REQUEST_STATUS_COLORS[value]}>{value}</Tag>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "created_date",
      key: "created_date",
      width: 165,
      render: (value) => moment(value).format("YYYY-MM-DD HH:mm"),
    },
  ], [openDetail, showFacilityColumn, showRequesterColumn]);

  const facilityFilter = facilities.length > 0 ? () => (
    <ResponsiveSelect
      name="facility"
      popupTitle="Select Facility"
      placeholder="Select facilities"
      showSearch
      mode="multiple"
      value={facilityIds}
      onChange={onFacilityChange}
      options={facilities.map((item) => ({ value: item.id, label: item.name }))}
      style={{ width: 260, maxWidth: "100%" }}
    />
  ) : undefined;

  const pageHeader = (
    <PageHeader
      hide_imports
      hide_search
      hid_new={!createPath}
      new_text="Create Service Request"
      onNew={createPath ? () => navigate(createPath) : undefined}
      handleFresh={() => actionRef.current?.reload?.()}
      renderFilter={facilityFilter}
      filterHasValue={facilityIds.length > 0}
    />
  );

  return (
    <div className="service-request-list-page">
      <ResizableProTable
        actionRef={actionRef}
        rowKey="id"
        columns={columns}
        request={request}
        search={false}
        options={false}
        rowSelection={false}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        scroll={{ x: showFacilityColumn || showRequesterColumn ? 1260 : 1100 }}
        toolBarRender={() => pageHeader}
        mobileCardHeaderRender={(record) => (
          <div className="service-request-mobile-card-header">
            <button
              type="button"
              className="service-request-mobile-card-title"
              onClick={() => openDetail(record)}
            >
              {record.title}
            </button>
            <Tag color={SERVICE_REQUEST_STATUS_COLORS[record.status]}>{record.status}</Tag>
          </div>
        )}
        hideMobileColumnActions
        mobileExpandedExcludeKeys={["title", "status"]}
        locale={{ emptyText: facilityIds.length ? "No service requests" : "Select one or more facilities" }}
      />
    </div>
  );
}
